import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../../components/Buttons/Button'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { VehicleHeader } from '../../components/VehicleHeader/VehicleHeader'
import {
  addPlanDraftStep,
  fetchChargingFeedbackMessagesThunk,
  executeChargingPlanThunk,
  fetchCalculationDataThunk,
  fetchTelemetryMessagesThunk,
  removePlanDraftStep,
  setPlanDraftStart,
  setPlanDraftStep,
  setTelemetryFrom,
  setVin,
  startChargingThunk,
  stopChargingThunk,
} from '../../features/chargingSteering/chargingSteeringSlice'
import {
  selectChargingSteeringActionLoading,
  selectChargingSteeringCalculationData,
  selectChargingSteeringCalculationLoading,
  selectChargingSteeringExecuteLoading,
  selectChargingSteeringFeedbackError,
  selectChargingSteeringFeedbackLoading,
  selectChargingSteeringFeedbackMessages,
  selectChargingSteeringLastResponse,
  selectChargingSteeringPlanDraft,
  selectChargingSteeringTelemetryError,
  selectChargingSteeringTelemetryFrom,
  selectChargingSteeringTelemetryLoading,
  selectChargingSteeringTelemetryMessages,
  selectChargingSteeringVin,
} from '../../features/chargingSteering/selectors/chargingSteeringSelectors'
import { selectCurrentVehicle } from '../../features/vehicle/selectors/vehicleSelectors'
import {
  calculatePlanEndTime,
  calculatePlanTotalEnergyWh,
  calculatePlanTotalMinutes,
  toChargingPlanRequest,
  validatePlanDraft,
} from '../../features/chargingSteering/utils/chargingPlan.utils'
import { clampTelemetryFromValue } from '../../features/chargingSteering/utils/telemetryDate.utils'
import { ActionButtons } from '../../components/ChargingSteering/ActionButtons'
import { CalculationDataPanel } from '../../components/ChargingSteering/CalculationDataPanel'
import { ChargingPlanEditor } from '../../components/ChargingSteering/ChargingPlanEditor'
import { TelemetryMessagesPanel } from '../../components/ChargingSteering/TelemetryMessagesPanel'
import { SteeringModeBar } from '../../components/ChargingSteering/SteeringModeBar'
import { InfoBlock } from '../../components/InfoBlock/InfoBlock'
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator'
import { showError } from '../../utils/ErrorHandler'
import './styles.css'

type ChargingSteeringLocationState = {
  vin?: string
}

type ChargingSteeringViewMode = 'actions' | 'chargingPlan'

const ChargingSteeringPage: FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [isCalculationDataVisible, setIsCalculationDataVisible] = useState(false)
  const [viewMode, setViewMode] = useState<ChargingSteeringViewMode>('actions')

  const vehicle = useAppSelector(selectCurrentVehicle)
  const vin = useAppSelector(selectChargingSteeringVin)
  const planDraft = useAppSelector(selectChargingSteeringPlanDraft)
  const calculationData = useAppSelector(selectChargingSteeringCalculationData)
  const actionLoading = useAppSelector(selectChargingSteeringActionLoading)
  const calculationLoading = useAppSelector(selectChargingSteeringCalculationLoading)
  const executeLoading = useAppSelector(selectChargingSteeringExecuteLoading)
  const lastResponse = useAppSelector(selectChargingSteeringLastResponse)
  const telemetryFrom = useAppSelector(selectChargingSteeringTelemetryFrom)
  const telemetryMessages = useAppSelector(selectChargingSteeringTelemetryMessages)
  const telemetryLoading = useAppSelector(selectChargingSteeringTelemetryLoading)
  const telemetryError = useAppSelector(selectChargingSteeringTelemetryError)
  const chargingFeedbackMessages = useAppSelector(selectChargingSteeringFeedbackMessages)
  const chargingFeedbackLoading = useAppSelector(selectChargingSteeringFeedbackLoading)
  const chargingFeedbackError = useAppSelector(selectChargingSteeringFeedbackError)

  const targetVin = useMemo(() => {
    const stateVin = (location.state as ChargingSteeringLocationState | null)?.vin
    const queryVin = new URLSearchParams(location.search).get('vin')
    return (stateVin ?? queryVin ?? vehicle?.vin ?? '').trim().toUpperCase()
  }, [location.search, location.state, vehicle?.vin])

  const selectedVin = vin.trim().toUpperCase()
  const isVinReady = selectedVin.length > 0
  const disableActionButtons = !isVinReady || actionLoading !== null
  const hasCalculationDataForVin = calculationData?.vin.trim().toUpperCase() === selectedVin
  const isCalculationDataButtonLoading = !isCalculationDataVisible && calculationLoading

  useEffect(() => {
    if (targetVin && targetVin !== selectedVin) {
      dispatch(setVin(targetVin))
    }
  }, [dispatch, selectedVin, targetVin])

  useEffect(() => {
    if (!isVinReady) return
    if (targetVin && targetVin !== selectedVin) return

    void dispatch(fetchTelemetryMessagesThunk())
    void dispatch(fetchChargingFeedbackMessagesThunk())
  }, [dispatch, isVinReady, selectedVin, targetVin])

  const totalMinutes = useMemo(() => calculatePlanTotalMinutes(planDraft.steps), [planDraft.steps])
  const totalEnergyWh = useMemo(
    () => calculatePlanTotalEnergyWh(planDraft.steps),
    [planDraft.steps],
  )
  const endTime = useMemo(
    () => calculatePlanEndTime(planDraft.start, totalMinutes),
    [planDraft.start, totalMinutes],
  )

  const handleExecute = async () => {
    if (!isVinReady) return

    const validation = validatePlanDraft(planDraft, calculationData?.allowedChargingPower)
    if (!validation.success) {
      showError({ code: 'INVALID_PLAN', message: validation.errorMessage })
      return
    }

    await dispatch(executeChargingPlanThunk(toChargingPlanRequest(planDraft)))
  }

  const handleToggleCalculationData = async () => {
    if (!isCalculationDataVisible && isVinReady && !hasCalculationDataForVin) {
      await dispatch(fetchCalculationDataThunk())
    }
    setIsCalculationDataVisible((current) => !current)
  }

  const handleCommitMessageFeedFrom = (value: string) => {
    if (!isVinReady) {
      return
    }

    const clampedFrom = clampTelemetryFromValue(value)

    if (clampedFrom !== telemetryFrom) {
      dispatch(setTelemetryFrom(clampedFrom))
    }

    void dispatch(fetchTelemetryMessagesThunk())
    void dispatch(fetchChargingFeedbackMessagesThunk())
  }

  const handleRefreshMessageFeed = () => {
    handleCommitMessageFeedFrom(telemetryFrom)
  }

  return (
    <div className="actions-page-wrapper" aria-label="Charging steering page" role="region">
      <div className="actions-page-header">
        <VehicleHeader vehicle={vehicle} onChangeVehicle={() => navigate('/vehicles')} />
      </div>

      <SteeringModeBar
        mode="f3"
        onSwitch={() =>
          navigate(`/feature2-charging-steering?vin=${encodeURIComponent(selectedVin)}`)
        }
      />

      <h1 className="actions-page-section-title">Actions</h1>

      {lastResponse && (
        <section className="actions-page-command-message" aria-live="polite">
          <InfoBlock title={lastResponse.title} description={lastResponse.description} />
        </section>
      )}

      <Button
        onClick={() => void handleToggleCalculationData()}
        variant="secondary"
        disabled={!isVinReady || isCalculationDataButtonLoading}
      >
        {isCalculationDataButtonLoading ? (
          <LoadingIndicator style="INLINE" />
        ) : isCalculationDataVisible ? (
          'Hide Calculation Data'
        ) : (
          'Show Calculation Data'
        )}
      </Button>

      {isCalculationDataVisible && (
        <CalculationDataPanel
          calculationData={calculationData}
          loading={calculationLoading}
          onRefresh={() => void dispatch(fetchCalculationDataThunk())}
        />
      )}

      {viewMode === 'actions' && (
        <Button
          variant="secondary"
          onClick={() => setViewMode('chargingPlan')}
          disabled={!isVinReady}
        >
          Generate Charging Plan
        </Button>
      )}

      {viewMode === 'actions' && (
        <ActionButtons
          disableActionButtons={disableActionButtons}
          actionLoading={actionLoading}
          onStart={() => void dispatch(startChargingThunk())}
          onStop={() => void dispatch(stopChargingThunk())}
        />
      )}

      {viewMode === 'chargingPlan' && (
        <ChargingPlanEditor
          start={planDraft.start}
          steps={planDraft.steps}
          allowedChargingPower={calculationData?.allowedChargingPower}
          executeLoading={executeLoading}
          isVinReady={isVinReady}
          endTime={endTime}
          totalEnergyWh={totalEnergyWh}
          onChangeStart={(value) => dispatch(setPlanDraftStart(value))}
          onAddStep={() => dispatch(addPlanDraftStep())}
          onRemoveStep={(index) => dispatch(removePlanDraftStep(index))}
          onChangeStep={(index, field, value) =>
            dispatch(setPlanDraftStep({ index, field, value }))
          }
          onExecute={() => void handleExecute()}
          onClose={() => setViewMode('actions')}
        />
      )}

      <TelemetryMessagesPanel
        from={telemetryFrom}
        messages={telemetryMessages}
        feedbackMessages={chargingFeedbackMessages}
        loading={telemetryLoading}
        feedbackLoading={chargingFeedbackLoading}
        error={telemetryError}
        feedbackError={chargingFeedbackError}
        isVinReady={isVinReady}
        onChangeFrom={(value) => dispatch(setTelemetryFrom(value))}
        onCommitFrom={handleCommitMessageFeedFrom}
        onRefresh={handleRefreshMessageFeed}
      />
    </div>
  )
}

export default ChargingSteeringPage
